export interface DatedRow {
  date: string;
  sourceKey: string;
  raw: unknown;
}

export interface SourceCursorState {
  cursor: string | null;
  buffer: DatedRow[];
  totalCount: number;
  consumedCount: number;
  exhausted: boolean;
}

export interface MergeState {
  sources: Record<string, SourceCursorState>;
}

export type FetchSourceFn = (cursor: string | null, limit: number) => Promise<DatedRow[]>;
export type CountSourceFn = () => Promise<number>;

export interface MergePageResult {
  items: DatedRow[];
  nextState: MergeState;
  hasNextPage: boolean;
}

export const PAGE_SIZE = 10;
export const FETCH_CHUNK_SIZE = 20;

export async function buildInitialMergeState(
  countFns: Record<string, CountSourceFn>,
): Promise<MergeState> {
  const entries = await Promise.all(
    Object.entries(countFns).map(async ([sourceKey, count]) => {
      const totalCount = await count();
      const state: SourceCursorState = {
        cursor: null,
        buffer: [],
        totalCount,
        consumedCount: 0,
        exhausted: totalCount === 0,
      };
      return [sourceKey, state] as const;
    }),
  );

  return { sources: Object.fromEntries(entries) };
}

async function topUpSource(
  state: SourceCursorState,
  fetchFn: FetchSourceFn | undefined,
  pageSize: number,
): Promise<SourceCursorState> {
  if (!fetchFn || state.exhausted || state.buffer.length >= pageSize) {
    return state;
  }

  const rows = await fetchFn(state.cursor, FETCH_CHUNK_SIZE);
  const buffer = [...state.buffer, ...rows];
  const cursor = rows.length > 0 ? rows[rows.length - 1].date : state.cursor;
  const exhausted =
    rows.length < FETCH_CHUNK_SIZE || state.consumedCount + buffer.length >= state.totalCount;

  return { ...state, buffer, cursor, exhausted };
}

export async function fetchMergePage(
  state: MergeState,
  fetchFns: Record<string, FetchSourceFn>,
  pageSize: number = PAGE_SIZE,
): Promise<MergePageResult> {
  const sourceKeys = Object.keys(state.sources);

  const toppedUpEntries = await Promise.all(
    sourceKeys.map(async (sourceKey) => {
      const next = await topUpSource(state.sources[sourceKey], fetchFns[sourceKey], pageSize);
      return [sourceKey, next] as const;
    }),
  );
  const toppedUp: Record<string, SourceCursorState> = Object.fromEntries(toppedUpEntries);

  const nonExhaustedWithBuffer = sourceKeys.filter(
    (key) => !toppedUp[key].exhausted && toppedUp[key].buffer.length > 0,
  );

  const safeCutoff =
    nonExhaustedWithBuffer.length > 0
      ? nonExhaustedWithBuffer.reduce<string | null>((min, key) => {
          const oldest = toppedUp[key].buffer[toppedUp[key].buffer.length - 1].date;
          return min === null || oldest < min ? oldest : min;
        }, null)
      : null;

  const candidates: Array<{ sourceKey: string; row: DatedRow }> = [];
  sourceKeys.forEach((sourceKey) => {
    toppedUp[sourceKey].buffer.forEach((row) => {
      if (safeCutoff === null || row.date >= safeCutoff) {
        candidates.push({ sourceKey, row });
      }
    });
  });
  candidates.sort((a, b) => (a.row.date < b.row.date ? 1 : a.row.date > b.row.date ? -1 : 0));

  const emitted = candidates.slice(0, pageSize);
  const emittedCountBySource: Record<string, number> = {};
  emitted.forEach(({ sourceKey }) => {
    emittedCountBySource[sourceKey] = (emittedCountBySource[sourceKey] ?? 0) + 1;
  });

  const nextSources: Record<string, SourceCursorState> = {};
  sourceKeys.forEach((sourceKey) => {
    const emittedCount = emittedCountBySource[sourceKey] ?? 0;
    const current = toppedUp[sourceKey];
    nextSources[sourceKey] = {
      ...current,
      buffer: current.buffer.slice(emittedCount),
      consumedCount: current.consumedCount + emittedCount,
    };
  });

  const hasNextPage = sourceKeys.some(
    (key) => nextSources[key].buffer.length > 0 || !nextSources[key].exhausted,
  );

  return {
    items: emitted.map((candidate) => candidate.row),
    nextState: { sources: nextSources },
    hasNextPage,
  };
}
